import React, { useEffect, useMemo, useRef, useState } from 'react';
import packagingWarehouse from '../class/packagingWarehouse';
import { formatRpcError } from '../class/jrpc20WSClient';
import { fmtQty, perColloOf } from '../class/util';
import swal from 'sweetalert';
import PkgSelect from './component/PkgSelect';
import PositionPicker from './component/PositionPicker';
import StockResultsTable from './component/StockResultsTable';
import PkgModal from './component/PkgModal';

// 'apertura' = un collo intero è stato aperto e i pezzi che restano sono diventati una riga.
// Non è un prelievo: sta fuori dalle somme prelevato/rientrato, e va etichettato come tale.
const TYPE_LABELS = { posizionamento: 'Posizionamento', prelievo: 'Prelievo', rientro: 'Rientro', apertura: 'Apertura collo', rettifica: 'Rettifica' };

// Effetto di un movimento SUL BUCKET giusto: lo storico vecchio mostrava un solo numero e non
// distingueva un movimento di colli interi da uno di colli aperti. Ora la riga dice su quale dei
// due ha agito e con quale variazione (colli e pezzi, con segno).
const signed = (n) => (Number(n) > 0 ? '+' : '') + fmtQty(n);
function movRowLabel(m) {
    return Number(m.is_partial) ? 'Collo aperto' : 'Colli interi';
}
function movDelta(m) {
    const isPart = Number(m.is_partial);
    const colli = Number(isPart ? m.delta_colli_partial : m.delta_colli_whole) || 0;
    const pieces = Number(isPart ? m.delta_pieces_partial : m.delta_pieces_whole) || 0;
    const parts = [];
    if (colli !== 0) parts.push(`${signed(colli)} ${Math.abs(colli) === 1 ? 'collo' : 'colli'}`);
    if (pieces !== 0) parts.push(`${signed(pieces)} pz`);
    return parts.length ? parts.join(' · ') : '—';
}

const PAGE_SIZE = 50;
const EMPTY_SEG = { area: '', scaffale: '', posizione: '', piano: '' };

// Storico generale: quanti movimenti si chiedono e come si descrive la scala. I filtri si applicano
// in SQL, non su queste righe: filtrare a valle del limite cercherebbe solo negli ultimi N movimenti
// mostrando il risultato come se fosse tutto lo storico.
const STORICO_ALL_LIMIT = 200;
const STORICO_ALL_SUBTITLE = `Tutto il magazzino — ultimi ${STORICO_ALL_LIMIT} movimenti`;
const EMPTY_STORICO_FILTERS = {
    from: '', to: '', movement_type: '', component_code: '', lotto: '',
    position_cella: '', order_production_number: '',
};

// I filtri sopravvivono al reload: il portale gira in kiosk su tablet, dove un ricaricamento non è
// un gesto deliberato dell'operatore, e ritrovare i filtri azzerati senza averli toccati confonde.
// Stessa promessa del gestionale, che li tiene in localStorage.
const STORICO_LS_KEY = 'pkg_storico_filters_portal';

function storicoFiltersLoad() {
    try {
        const raw = localStorage.getItem(STORICO_LS_KEY);
        const v = raw ? JSON.parse(raw) : null;
        // Forma vecchia o manomessa: si riparte da vuoto invece di propagare campi mancanti negli
        // input controllati (un `value={undefined}` rende l'input non controllato).
        return (v && typeof v === 'object') ? { ...EMPTY_STORICO_FILTERS, ...v } : EMPTY_STORICO_FILTERS;
    } catch (e) {
        return EMPTY_STORICO_FILTERS;
    }
}
function storicoFiltersSave(v) {
    try { localStorage.setItem(STORICO_LS_KEY, JSON.stringify(v || {})); } catch (e) { /* noop */ }
}

// Scorciatoie di intervallo: sul tablet digitare due date è la cosa più lenta che si possa chiedere.
// Stessi quattro intervalli del pannello d'ufficio, calcolati in ora LOCALE (un toISOString darebbe
// il giorno UTC, cioè quello sbagliato nelle ore serali con il fuso italiano).
function quickRange(token) {
    const now = new Date();
    const iso = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
    switch (token) {
        case 'today': return [iso(now), iso(now)];
        case 'yesterday': return [iso(addDays(now, -1)), iso(addDays(now, -1))];
        case 'this_week': {
            const dow = now.getDay() === 0 ? 6 : now.getDay() - 1;   // settimana da lunedì
            return [iso(addDays(now, -dow)), iso(addDays(now, 6 - dow))];
        }
        case 'this_month':
            return [iso(new Date(now.getFullYear(), now.getMonth(), 1)),
                    iso(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
        default: return null;
    }
}

// Dalla forma FORM (piatta, comoda per gli input) al contratto del webservice. Le chiavi vuote non
// si inviano: il BE le tratterebbe come filtro vuoto, ma mandarle sporca la richiesta e nasconde
// quali filtri sono davvero attivi. `movement_type` è un array anche con un solo valore, così il
// contratto è lo stesso della multiselezione del gestionale.
// `scope` = 'article' quando lo storico è già ristretto a un articolo/divisione/lotto: codice e lotto
// li fissa la scala, e mandarli da lì significherebbe filtrare due volte la stessa cosa (o filtrare
// con un valore contraddittorio, ottenendo zero righe senza capire perché).
function toWsFilters(f, scope) {
    const out = {};
    if (f.from || f.to) { out.movement_date = { from: f.from || '', to: f.to || '' }; }
    if (f.movement_type) { out.movement_type = [f.movement_type]; }
    const keys = scope === 'article'
        ? ['position_cella', 'order_production_number']
        : ['component_code', 'lotto', 'position_cella', 'order_production_number'];
    keys.forEach((k) => {
        if (f[k] && f[k].trim()) { out[k] = f[k].trim(); }
    });
    return out;
}
const MODES = [
    { key: 'articolo', label: 'Per articolo' },
    { key: 'posizione', label: 'Per posizione' },
    { key: 'magazzino', label: 'Tutto il magazzino' },
];

// Colonne di contesto per modalità: si omette sempre ciò che è già fissato dai filtri.
const COLUMNS = {
    articolo: ['position', 'lotto'],
    magazzino: ['position', 'component', 'division'],
};

// I segmenti posizione vanno al BE come colonne separate, non come prefisso di stringa:
// `area` è testo libero, quindi un LIKE 'A%' pescherebbe anche l'area "AB".
const segFilter = (s) => ({
    ...(s.area ? { area: s.area } : {}),
    ...(s.scaffale ? { scaffale: s.scaffale } : {}),
    ...(s.posizione ? { posizione: s.posizione } : {}),
    ...(s.piano ? { piano: s.piano } : {}),
});

const matchesSeg = (p, s) =>
    (!s.area || p.area === s.area) && (!s.scaffale || p.scaffale === s.scaffale) &&
    (!s.posizione || p.posizione === s.posizione) && (!s.piano || p.piano === s.piano);

// Ordinamento per segmenti e non per la stringa `cella`: le aree hanno lunghezza diversa
// ("A" vs "AB") e l'ordine alfabetico della stringa composta metterebbe "AB-01-01-0" in
// mezzo alle celle dell'area A. Scaffale/posizione/piano sono zero-padded, quindi il
// confronto numeric-aware dà l'ordine di percorrenza fisica della corsia.
const plural = (n, one, many) => (Number(n) === 1 ? one : many);

const byNum = (a, b) => String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });
const cellCmp = (a, b) =>
    byNum(a.area, b.area) || byNum(a.scaffale, b.scaffale) ||
    byNum(a.posizione, b.posizione) || byNum(a.piano, b.piano);

// Modalità posizione: le giacenze si scaricano tutte in una volta e si impaginano lato
// client insieme alle celle. Nel magazzino reale le celle (1850) superano le righe di
// giacenza di ordini di grandezza, quindi il volume da reggere è quello delle celle —
// che sono già in memoria da wsGetPositions. Il cap è una rete di sicurezza: se una
// singola area superasse 1000 righe di giacenza lo diciamo invece di troncare in silenzio.
const FETCH_PAGE = 200;   // = limite massimo accettato dal BE
const MAX_FETCH = 1000;

async function fetchAllStock(ws, filters) {
    const rows = [];
    let total = 0, totals = null, offset = 0;
    for (;;) {
        const r = await ws.searchStock({ ...filters, limit: FETCH_PAGE, offset });
        total = Number(r.total) || 0;
        if (!totals) totals = r.totals;
        const batch = r.rows || [];
        rows.push(...batch);
        offset += FETCH_PAGE;
        if (!batch.length || rows.length >= total || offset >= MAX_FETCH) break;
    }
    return { rows, total, totals, complete: rows.length >= total };
}

// Consultazione (sola lettura) a 3 modalità:
//  - per Articolo: Cliente → Divisione → Codice (flusso storico), paginato dal BE
//  - per Posizione: cella esatta o parziale — elenco delle CELLE col loro contenuto,
//    incluse quelle vuote, paginato lato client
//  - Tutto il magazzino: stato attuale con filtri facoltativi, paginato dal BE
//    (qui le celle vuote restano escluse: sarebbero 1848 su 1850 e coprirebbero i dati)
const ConsultazionePage = () => {
    const ws = new packagingWarehouse();

    const [mode, setMode] = useState('articolo');

    // Cataloghi, caricati una volta sola in mount.
    const [customers, setCustomers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [positions, setPositions] = useState([]);
    const [components, setComponents] = useState([]);

    // Filtri.
    const [customerUuid, setCustomerUuid] = useState('');
    const [divisionUuid, setDivisionUuid] = useState('');
    const [componentUuid, setComponentUuid] = useState('');
    const [posCode, setPosCode] = useState('');       // cella o prefisso ('A', 'A-01', 'A-01-01-0')
    const [posSeg, setPosSeg] = useState(EMPTY_SEG);  // gli stessi segmenti, scomposti
    const [areaFilter, setAreaFilter] = useState('');
    const [showEmpty, setShowEmpty] = useState(true); // modalità posizione: celle vuote in elenco
    // Dialog dei colli aperti: una TABELLA, non un elenco di testo (una swal non la regge).
    const [partials, setPartials] = useState(null); // { row, rows|null } — rows null = caricamento
    // Storico: { title, subtitle, rows|null, all? } — rows null = caricamento, all = storico generale
    const [storico, setStorico] = useState(null);
    // Filtri dello storico generale, forma FORM (piatta, un campo per input). Si applicano con
    // "Applica", non a ogni battuta: ogni applicazione è una chiamata al server.
    const [storicoForm, setStoricoForm] = useState(storicoFiltersLoad);

    const [page, setPage] = useState(0);
    const [result, setResult] = useState(null);       // articolo/magazzino: pagina dal BE
    const [posStock, setPosStock] = useState(null);   // posizione: TUTTE le giacenze sotto il filtro
    const [loading, setLoading] = useState(false);

    // Il call_id JSON-RPC è per-richiesta, quindi le due letture in mount possono andare in parallelo.
    useEffect(() => {
        Promise.all([ws.getDivisionCatalog(), ws.getPositions()])
            .then(([cat, pos]) => {
                setCustomers(cat.customers || []);
                setDivisions(cat.divisions || []);
                setPositions(pos.positions || []);
            })
            .catch(err => swal('Errore', formatRpcError(err), 'error'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const divisionsForCustomer = divisions.filter(d => String(d.customer_uuid) === String(customerUuid));
    const areas = useMemo(() => [...new Set(positions.map(p => p.area))].sort(
        (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })), [positions]);

    // Stato UI → call_data. Ritorna null quando i filtri obbligatori della modalità mancano
    // ("non cercare ancora"). È l'unico punto di mappatura: niente filtri di una modalità
    // che sopravvivono in un'altra.
    const buildFilters = () => {
        if (mode === 'articolo') {
            // Serve SOLO l'articolo: cliente e divisione sono restrizioni facoltative.
            if (!componentUuid) return null;
            return {
                component_uuid: componentUuid,
                ...(customerUuid ? { customer_uuid: customerUuid } : {}),
                ...(divisionUuid ? { division_uuid: divisionUuid } : {}),
            };
        }
        return {
            ...(customerUuid ? { customer_uuid: customerUuid } : {}),
            ...(divisionUuid ? { division_uuid: divisionUuid } : {}),
            ...(areaFilter ? { area: areaFilter } : {}),
        };
    };

    // Anti-race: sul WiFi del reparto due ricerche ravvicinate possono tornare fuori ordine
    // e lasciare a video le righe della richiesta vecchia. Vince sempre l'ultima partita.
    const reqIdRef = useRef(0);

    // Identità del filtro posizione corrente. `posSeg` cambia in modo sincrono al tocco mentre
    // il fetch è asincrono: senza questa chiave il merge accoppierebbe le celle NUOVE con le
    // giacenze VECCHIE, e una cella piena verrebbe mostrata come "Cella vuota".
    const posKey = `${posCode}|${divisionUuid}`;

    // Articolo / Magazzino: una pagina per volta dal BE.
    //
    // Il guard di modalità va PRIMA del bump: `page` è fra le dipendenze di questo effetto ma
    // non dell'altro, quindi bumpare anche in modalità posizione ucciderebbe il fetch in volo
    // a ogni cambio pagina client-side, senza che nessuno lo rilanci.
    // Il bump invece va PRIMA dell'uscita "non cercare": azzerare i filtri deve invalidare la
    // richiesta in volo, altrimenti la sua risposta rimette a video i risultati appena tolti.
    useEffect(() => {
        if (mode === 'posizione') return;
        const reqId = ++reqIdRef.current;

        const filters = buildFilters();
        if (!filters) { setResult(null); setLoading(false); return; }

        setLoading(true);
        ws.searchStock({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE })
            .then(r => { if (reqId === reqIdRef.current) setResult(r); })
            .catch(err => {
                if (reqId !== reqIdRef.current) return;
                // Niente risultati vecchi sotto filtri nuovi: verrebbero letti come la risposta.
                setResult(null);
                swal('Errore', formatRpcError(err), 'error');
            })
            .finally(() => { if (reqId === reqIdRef.current) setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, customerUuid, divisionUuid, componentUuid, areaFilter, page]);

    // Posizione: scarica TUTTE le giacenze sotto il filtro (non una pagina), perché la
    // paginazione qui è sulle celle e una cella può stare a cavallo di due pagine di righe.
    useEffect(() => {
        if (mode !== 'posizione') return;
        const reqId = ++reqIdRef.current;

        if (!posSeg.area) { setPosStock(null); setLoading(false); return; }

        setLoading(true);
        fetchAllStock(ws, { ...segFilter(posSeg), ...(divisionUuid ? { division_uuid: divisionUuid } : {}) })
            // `key`: identifica il filtro che ha prodotto queste righe, così il merge non può
            // usarle sotto un filtro diverso mentre il refetch è ancora in corso.
            .then(r => { if (reqId === reqIdRef.current) setPosStock({ ...r, key: posKey }); })
            .catch(err => {
                if (reqId !== reqIdRef.current) return;
                setPosStock(null);
                swal('Errore', formatRpcError(err), 'error');
            })
            .finally(() => { if (reqId === reqIdRef.current) setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, posCode, divisionUuid]);

    // ── Handler filtri (ogni cambio riporta a pagina 1, altrimenti si atterra su una pagina vuota) ──

    const onModeChange = (m) => {
        setMode(m); setPage(0); setResult(null);
        setCustomerUuid(''); setDivisionUuid(''); setComponentUuid(''); setComponents([]);
        setPosCode(''); setPosSeg(EMPTY_SEG); setAreaFilter('');
    };

    // Ricerca per articolo: cliente e divisione sono FILTRI OPZIONALI, non prerequisiti — si può
    // cercare un codice in tutto il magazzino. La tendina elenca i codici con giacenza, ristretti
    // ai filtri quando ci sono.
    useEffect(() => {
        if (mode !== 'articolo') return;
        let alive = true;
        ws.getComponentsWithStock({ division_uuid: divisionUuid || undefined, customer_uuid: customerUuid || undefined })
            .then(r => { if (alive) setComponents(r.components || []); })
            .catch(err => { if (alive) swal('Errore', formatRpcError(err), 'error'); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, divisionUuid, customerUuid]);

    const onCustomerChange = (v) => {
        // Cambiando cliente la divisione scelta non è più coerente; l'articolo resta (è un filtro
        // indipendente) e se non ha giacenza sotto i nuovi filtri il risultato sarà vuoto — che è
        // l'informazione corretta.
        setCustomerUuid(v); setDivisionUuid(''); setPage(0);
    };

    const onDivisionChange = (v) => {
        setDivisionUuid(v); setPage(0);
    };

    const onComponentChange = (v) => { setComponentUuid(v); setPage(0); };
    const onPositionChange = (code, seg) => { setPosCode(code); setPosSeg(seg || EMPTY_SEG); setPage(0); };
    const onAreaChange = (v) => { setAreaFilter(v); setPage(0); };
    const onShowEmptyChange = (v) => { setShowEmpty(v); setPage(0); };

    // ── Storico ──

    // Storico di un ARTICOLO in una divisione e un lotto: scala VOLUTAMENTE più larga della riga da
    // cui si apre. Posizione e pezzatura non entrano — il materiale cambia cella e la pezzatura
    // spezza le righe, quindi la storia di un materiale fisico stava a frammenti su più righe e i
    // movimenti "mancanti" sembravano persi. Comprende i colli aperti, anche quelli esauriti.
    const showStorico = async (row, filters) => {
        // Filtrabile come quello generale: anche la storia di un solo articolo diventa lunga, e
        // restringerla per data, tipo, cella o ordine è la stessa esigenza. `row` resta in stato per
        // poter rifare la richiesta quando i filtri cambiano.
        const f = filters || storicoForm;
        setStoricoForm(f);
        storicoFiltersSave(f);
        const subtitle = [
            row.component_code, row.division_name,
            row.lotto ? `lotto ${row.lotto}` : null,
            'tutte le celle e pezzature',
        ].filter(Boolean).join(' · ');
        const base = { title: 'Storico articolo', subtitle, scope: 'article', row };
        setStorico({ ...base, rows: null });
        try {
            const r = await ws.getArticleMovements({
                division_uuid: row.division_uuid,
                component_uuid: row.component_uuid,
                lotto: row.lotto || '',
                filters: toWsFilters(f, 'article'),
            });
            setStorico({ ...base, rows: r.movements || [] });
        } catch (err) {
            setStorico(null);
            swal('Errore', formatRpcError(err), 'error');
        }
    };

    // Storico GENERALE del magazzino, filtrabile. I filtri si applicano in SQL: la query è limitata,
    // e filtrare a valle del limite cercherebbe solo negli ultimi 200 movimenti mostrando il
    // risultato come se fosse tutto lo storico.
    const showStoricoAll = async (form) => {
        const f = form || EMPTY_STORICO_FILTERS;
        setStoricoForm(f);
        storicoFiltersSave(f);
        setStorico({ title: 'Storico movimenti', subtitle: STORICO_ALL_SUBTITLE, rows: null, scope: 'all' });
        try {
            const r = await ws.getAllMovements(STORICO_ALL_LIMIT, toWsFilters(f, 'all'));
            const rows = r.movements || [];
            // Risultato al tetto = troncato, e va detto: un elenco tagliato si legge come completo.
            const truncated = rows.length >= STORICO_ALL_LIMIT;
            setStorico({
                title: 'Storico movimenti',
                subtitle: STORICO_ALL_SUBTITLE + (truncated ? ' — ce ne sono altri oltre il limite, restringi con i filtri' : ''),
                rows, scope: 'all',
            });
        } catch (err) {
            setStorico(null);
            swal('Errore', formatRpcError(err), 'error');
        }
    };

    // Applica i filtri alla scala dello storico aperto: generale, oppure l'articolo di quella riga.
    const applyStoricoFilters = (f) => {
        if (storico && storico.scope === 'article') { showStorico(storico.row, f); }
        else { showStoricoAll(f); }
    };

    // ── Dettaglio colli aperti ──
    // In consultazione i colli aperti sono aggregati nella riga dei loro colli interi: qui si
    // vedono uno per uno, dal più vecchio (è l'ordine utile per decidere quale finire).
    const showPartials = async (row) => {
        // Si apre subito in caricamento: su tablet il tocco deve dare un riscontro immediato.
        setPartials({ row, rows: null });
        try {
            const r = await ws.getGroupPartials({
                position_uuid: row.position_uuid,
                division_uuid: row.division_uuid,
                component_uuid: row.component_uuid,
                lotto: row.lotto || '',
                qty_per_collo_whole: row.qty_per_collo_whole,
            });
            setPartials({ row, rows: r.partials || [] });
        } catch (err) {
            setPartials(null);
            swal('Errore', formatRpcError(err), 'error');
        }
    };

    // ── Derivati per il render ──

    // Modalità posizione: elenco ORIENTATO ALLE CELLE. Per ogni cella sotto il filtro si
    // emettono le sue righe di giacenza, oppure una riga sintetica "vuota". Le celle sono
    // già tutte in memoria, quindi il merge non costa nessuna chiamata.
    const posView = useMemo(() => {
        if (mode !== 'posizione' || !posStock || !posSeg.area) return null;
        // Righe di un filtro precedente: meglio niente tabella (c'è il banner "Caricamento…")
        // che una tabella che dà per vuote celle piene.
        if (posStock.key !== posKey) return null;

        const cells = positions.filter(p => matchesSeg(p, posSeg)).sort(cellCmp);
        const byCell = new Map();
        posStock.rows.forEach(r => {
            if (!byCell.has(r.position_cella)) byCell.set(r.position_cella, []);
            byCell.get(r.position_cella).push(r);
        });

        const rows = [];
        let occupied = 0, empty = 0;
        cells.forEach(p => {
            const rs = byCell.get(p.cella);
            if (rs && rs.length) {
                rows.push(...rs);
                occupied++;
                byCell.delete(p.cella);
            } else {
                empty++;
                if (showEmpty) rows.push({ __empty: true, stock_uuid: `empty-${p.uuid}`, position_cella: p.cella });
            }
        });
        // Giacenze su celle non presenti in anagrafica: in coda, mai perse. Contate a parte,
        // così `occupied + empty` resta sempre uguale al numero di celle e `empty` non va sotto zero.
        let orphans = 0;
        byCell.forEach(rs => { rows.push(...rs); orphans += rs.length; });

        return { rows, cells: cells.length, occupied, empty, orphans };
    }, [mode, posStock, posKey, positions, posSeg, showEmpty]);

    const isPos = mode === 'posizione';
    // In modalità posizione la Divisione è una colonna solo se non è già fissata dal filtro.
    const columns = isPos
        ? ['position', 'component', ...(divisionUuid ? [] : ['division']), 'lotto']
        : COLUMNS[mode];

    // Posizione: paginazione client-side sulle righe già in memoria.
    // Articolo/Magazzino: la pagina arriva già impaginata dal BE.
    const total = isPos ? (posView ? posView.rows.length : 0) : (result ? Number(result.total) || 0 : 0);
    const from = total ? page * PAGE_SIZE + 1 : 0;
    const to = Math.min((page + 1) * PAGE_SIZE, total);
    const hasPrev = page > 0;
    const hasNext = to < total;

    const viewRows = isPos
        ? (posView ? posView.rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE) : [])
        : (result ? result.rows || [] : []);
    // posView non è null solo se posStock corrisponde al filtro corrente: agganciando totali e
    // conteggi a posView non possono descrivere una ricerca diversa da quella a video.
    const viewTotals = isPos ? (posView ? posStock.totals : null) : (result ? result.totals : null);
    const hasResult = isPos ? posView !== null : result !== null;
    // Le somme coprono le righe di GIACENZA: in posizione `total` conta anche le celle vuote.
    const totalStockRows = isPos ? (posView ? posStock.total : 0) : total;

    return (
        <div className="pkg-page">
            <div className="pkg-page-header">
                <h2 className="pkg-title">Consultazione giacenze</h2>
                {/* Lo storico generale esisteva solo nel gestionale: nel portale mancava del tutto. */}
                {/* Arrow, non `onClick={showStoricoAll}`: così il primo argomento non è l'oggetto
                    Event del click, che finirebbe dritto nei filtri. Riapre con i filtri di prima,
                    come fa il gestionale che li ripesca da localStorage — e restano a schermo, quindi
                    visibili. */}
                <button className="btn-indietro" onClick={() => showStoricoAll(storicoForm)}>Storico movimenti</button>
            </div>

            <div className="pkg-tabs">
                {MODES.map(m => (
                    <button
                        key={m.key}
                        className={`pkg-tab${mode === m.key ? ' is-active' : ''}`}
                        onClick={() => onModeChange(m.key)}
                    >{m.label}</button>
                ))}
            </div>

            <div className="pkg-card">
                {mode === 'articolo' && (
                    <div className="pkg-row">
                        {/* PRIMO campo: è l'unico necessario e quello che fa partire la ricerca.
                            Cliente e divisione, sotto, la restringono. Sempre attivo. */}
                        <div className="pkg-field">
                            <label>Codice articolo</label>
                            <PkgSelect
                                value={componentUuid}
                                options={components.map(c => ({ value: c.uuid, label: `${c.code} - ${c.description}` }))}
                                onChange={onComponentChange}
                                placeholder={components.length ? 'Cerca articolo...' : 'Nessun articolo a giacenza'}
                                noOptionsText="Nessun articolo"
                            />
                        </div>
                        <div className="pkg-field">
                            <label>Cliente <span className="muted">(facoltativo)</span></label>
                            <PkgSelect
                                value={customerUuid}
                                options={customers.map(c => ({ value: c.uuid, label: c.name }))}
                                onChange={onCustomerChange}
                                placeholder="Tutti i clienti"
                                noOptionsText="Nessun cliente"
                            />
                        </div>
                        <div className="pkg-field">
                            <label>Divisione <span className="muted">(facoltativa)</span></label>
                            <PkgSelect
                                value={divisionUuid}
                                options={divisionsForCustomer.map(d => ({ value: d.uuid, label: d.name }))}
                                onChange={onDivisionChange}
                                isDisabled={!customerUuid}
                                placeholder={customerUuid ? 'Tutte le divisioni' : 'Seleziona prima il cliente'}
                                noOptionsText="Nessuna divisione"
                            />
                        </div>
                    </div>
                )}

                {mode === 'posizione' && (
                    <div className="pkg-row">
                        <div className="pkg-field pkg-field-pos">
                            <label>Posizione</label>
                            <PositionPicker positions={positions} value={posCode} onChange={onPositionChange} allowPartial />
                            <div className="pkg-hint">
                                Compila solo l'Area (o Area + Scaffale) per vedere tutte le celle sotto; completa i 4 segmenti per la singola cella.
                            </div>
                        </div>
                        <div className="pkg-field">
                            <label>Divisione <span className="muted">(facoltativa)</span></label>
                            <PkgSelect
                                value={divisionUuid}
                                options={divisions.map(d => ({ value: d.uuid, label: d.name }))}
                                onChange={onDivisionChange}
                                placeholder="Tutte le divisioni"
                                noOptionsText="Nessuna divisione"
                            />
                            <label className="pkg-check">
                                <input type="checkbox" checked={showEmpty} onChange={e => onShowEmptyChange(e.target.checked)} />
                                Elenca anche le celle vuote
                            </label>
                        </div>
                    </div>
                )}

                {mode === 'magazzino' && (
                    <div className="pkg-row">
                        {/* PRIMO campo: è l'unico necessario e quello che fa partire la ricerca.
                            Cliente e divisione, sotto, la restringono. Sempre attivo. */}
                        <div className="pkg-field">
                            <label>Codice articolo</label>
                            <PkgSelect
                                value={componentUuid}
                                options={components.map(c => ({ value: c.uuid, label: `${c.code} - ${c.description}` }))}
                                onChange={onComponentChange}
                                placeholder={components.length ? 'Cerca articolo...' : 'Nessun articolo a giacenza'}
                                noOptionsText="Nessun articolo"
                            />
                        </div>
                        <div className="pkg-field">
                            <label>Cliente <span className="muted">(facoltativo)</span></label>
                            <PkgSelect
                                value={customerUuid}
                                options={customers.map(c => ({ value: c.uuid, label: c.name }))}
                                onChange={onCustomerChange}
                                placeholder="Tutti i clienti"
                                noOptionsText="Nessun cliente"
                            />
                        </div>
                        <div className="pkg-field">
                            <label>Divisione <span className="muted">(facoltativa)</span></label>
                            <PkgSelect
                                value={divisionUuid}
                                options={(customerUuid ? divisionsForCustomer : divisions).map(d => ({ value: d.uuid, label: d.name }))}
                                onChange={onDivisionChange}
                                placeholder="Tutte le divisioni"
                                noOptionsText="Nessuna divisione"
                            />
                        </div>
                        <div className="pkg-field">
                            <label>Area <span className="muted">(facoltativa)</span></label>
                            <PkgSelect
                                value={areaFilter}
                                options={areas.map(a => ({ value: a, label: a }))}
                                onChange={onAreaChange}
                                isSearchable={false}
                                placeholder="Tutte le aree"
                                noOptionsText="Nessuna area"
                            />
                        </div>
                    </div>
                )}
            </div>

            {loading && <div className="pkg-banner pkg-banner-neutral">Caricamento…</div>}

            {/* Riepilogo di quel che si sta guardando: quante celle, quante occupate.
                Non sostituisce l'elenco — le celle vuote sono righe della tabella. */}
            {isPos && posView && (
                <div className="pkg-banner pkg-banner-info">
                    <strong>{posCode}</strong> · {fmtQty(posView.cells)} {plural(posView.cells, 'cella', 'celle')}
                    {' · '}{fmtQty(posView.occupied)} {plural(posView.occupied, 'occupata', 'occupate')}
                    {' · '}{fmtQty(posView.empty)} {plural(posView.empty, 'vuota', 'vuote')}
                    {posView.orphans > 0 && (
                        <> · {fmtQty(posView.orphans)} su celle non più in anagrafica</>
                    )}
                    {posStock && !posStock.complete && (
                        <> — <strong>attenzione:</strong> oltre {fmtQty(MAX_FETCH)} righe di giacenza, elenco parziale. Restringi il filtro.</>
                    )}
                </div>
            )}

            {hasResult && (
                <>
                    <StockResultsTable
                        rows={viewRows}
                        columns={columns}
                        totals={viewTotals}
                        totalRows={totalStockRows}
                        showPz={mode === 'articolo'}
                        onStorico={showStorico}
                        onPartials={showPartials}
                        emptyText={mode === 'articolo'
                            ? 'Nessuna giacenza per questo codice in questa divisione.'
                            : isPos
                                ? 'Nessuna cella corrisponde a questo filtro.'
                                : 'Nessuna giacenza con questi filtri.'}
                    />

                    {total > PAGE_SIZE && (
                        <div className="pkg-pager">
                            <button className="btn-indietro" disabled={!hasPrev || loading}
                                onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Precedente</button>
                            <span className="muted">Risultati {fmtQty(from)}–{fmtQty(to)} di {fmtQty(total)}</span>
                            <button className="btn-indietro" disabled={!hasNext || loading}
                                onClick={() => setPage(p => p + 1)}>Successiva ›</button>
                        </div>
                    )}
                </>
            )}

            {/* Dettaglio dei colli aperti di un gruppo: una TABELLA vera, come le altre del portale.
                La lista aggrega i colli nella riga dei loro interi, qui si vedono uno per uno. */}
            {/* Storico movimenti — tabella vera, e alla scala giusta:
                di gruppo dalla riga, generale dal bottone in testata. */}
            {storico && (
                <PkgModal title={storico.title} subtitle={storico.subtitle} onClose={() => setStorico(null)}>
                    {/* Filtri su entrambe le scale (generale e articolo). Nella scala articolo codice e
                        lotto NON compaiono: li fissa già la scala, e offrirli inviterebbe a digitare un
                        valore contraddittorio che restituisce zero righe senza spiegare perché.
                        Riuso di pkg-row/pkg-field e dei bottoni già in uso. */}
                    {storico.scope && (
                        <>
                            <div className="pkg-row">
                                {/* Le due date e le loro scorciatoie stanno in UN campo: così i quattro
                                    bottoni si distribuiscono sotto ENTRAMBI gli input invece di stare
                                    sotto il solo "Al". */}
                                <div className="pkg-field pkg-field-daterange">
                                    <div className="pkg-row pkg-row-pair">
                                        <div className="pkg-field">
                                            <label>Dal</label>
                                            <input type="date" value={storicoForm.from}
                                                onChange={e => setStoricoForm({ ...storicoForm, from: e.target.value })} />
                                        </div>
                                        <div className="pkg-field">
                                            <label>Al</label>
                                            <input type="date" value={storicoForm.to}
                                                onChange={e => setStoricoForm({ ...storicoForm, to: e.target.value })} />
                                        </div>
                                    </div>
                                    {/* Applicano subito: sono scorciatoie, non impostazioni da confermare. */}
                                    <div className="pkg-quick-range">
                                        {[['today', 'Oggi'], ['yesterday', 'Ieri'], ['this_week', 'Settimana'], ['this_month', 'Mese']].map(([tok, label]) => (
                                            <button key={tok} className="btn-sm btn-indietro" onClick={() => {
                                                const r = quickRange(tok);
                                                if (r) { applyStoricoFilters({ ...storicoForm, from: r[0], to: r[1] }); }
                                            }}>{label}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="pkg-field">
                                    <label>Tipo</label>
                                    <PkgSelect
                                        value={storicoForm.movement_type}
                                        options={Object.keys(TYPE_LABELS).map(k => ({ value: k, label: TYPE_LABELS[k] }))}
                                        onChange={v => setStoricoForm({ ...storicoForm, movement_type: v || '' })}
                                        placeholder="Tutti i tipi"
                                        noOptionsText="Nessun tipo"
                                    />
                                </div>
                            </div>
                            {storico.scope === 'all' && (
                            <div className="pkg-row">
                                <div className="pkg-field">
                                    <label>Codice articolo</label>
                                    <input value={storicoForm.component_code} placeholder="contiene..."
                                        onChange={e => setStoricoForm({ ...storicoForm, component_code: e.target.value })} />
                                </div>
                                <div className="pkg-field">
                                    <label>Lotto</label>
                                    <input value={storicoForm.lotto} placeholder="contiene..."
                                        onChange={e => setStoricoForm({ ...storicoForm, lotto: e.target.value })} />
                                </div>
                            </div>
                            )}
                            <div className="pkg-row">
                                {/* Posizione e ordine: allo scaffale sono le due domande più frequenti
                                    ("cosa è passato da questa cella", "cosa ha preso quest'ordine"). */}
                                <div className="pkg-field">
                                    <label>Posizione (cella)</label>
                                    <input value={storicoForm.position_cella} placeholder="contiene..."
                                        onChange={e => setStoricoForm({ ...storicoForm, position_cella: e.target.value })} />
                                </div>
                                <div className="pkg-field">
                                    <label>Ordine</label>
                                    <input value={storicoForm.order_production_number} placeholder="es. 2026.0042"
                                        onChange={e => setStoricoForm({ ...storicoForm, order_production_number: e.target.value })} />
                                </div>
                                {/* I bottoni stanno in un contenitore proprio: `.pkg-field` è una
                                    colonna flex, quindi da figli diretti si impilerebbero. */}
                                <div className="pkg-field">
                                    <label>&nbsp;</label>
                                    <div className="pkg-row-actions" style={{ justifyContent: 'flex-start' }}>
                                        <button className="btn-sm blue_butt" onClick={() => applyStoricoFilters(storicoForm)}>Applica</button>
                                        <button className="btn-sm btn-indietro" onClick={() => applyStoricoFilters(EMPTY_STORICO_FILTERS)}>Azzera</button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {storico.rows === null ? (
                        <p className="muted">Caricamento…</p>
                    ) : (
                        <div className="pkg-table-wrap">
                            <table>
                                <thead>
                                    <tr className="table-head">
                                        <th>Data</th>
                                        <th>Tipo</th>
                                        {/* Serve in entrambe le scale: nello storico generale per sapere di che
                                            articolo si parla, in quello di un articolo perché ora attraversa più
                                            celle e pezzature (prima era una cella sola e la colonna era inutile). */}
                                        <th>Articolo · Cella</th>
                                        <th>Su</th>
                                        <th className="text-right">Variazione</th>
                                        <th className="text-right">Movimentato</th>
                                        <th>Ordine</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {storico.rows.map((m) => (
                                        <tr key={m.uuid}>
                                            <td>
                                                {m.movement_date ? new Date(Number(m.movement_date) * 1000).toLocaleString('it-IT') : ''}
                                                {(m.employee_surname || m.employee_name) && (
                                                    <div className="muted pkg-cell-sub">
                                                        {[m.employee_surname, m.employee_name].filter(Boolean).join(' ')}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{TYPE_LABELS[m.movement_type] || m.movement_type}</td>
                                            <td>
                                                {m.component_code || <span className="muted">—</span>}
                                                {(m.position_cella || m.lotto) && (
                                                    <div className="muted pkg-cell-sub">
                                                        {[m.position_cella, m.lotto ? `lotto ${m.lotto}` : null].filter(Boolean).join(' · ')}
                                                    </div>
                                                )}
                                            </td>
                                            {/* La distinzione che lo storico vecchio non faceva. */}
                                            <td>{movRowLabel(m)}</td>
                                            <td className="text-right">{movDelta(m)}</td>
                                            <td className="text-right"><strong>{fmtQty(m.qty)}</strong> pz</td>
                                            <td>
                                                {m.order_production_number || <span className="muted">—</span>}
                                                {m.note && <div className="muted pkg-cell-sub">{m.note}</div>}
                                            </td>
                                        </tr>
                                    ))}
                                    {storico.rows.length === 0 && (
                                        <tr><td colSpan="7" className="muted" style={{ textAlign: 'center', padding: '20px' }}>Nessun movimento.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </PkgModal>
            )}

            {partials && (
                <PkgModal
                    title="Colli aperti"
                    subtitle={[
                        partials.row.position_cella,
                        partials.row.component_code,
                        partials.row.lotto ? `lotto ${partials.row.lotto}` : null,
                        perColloOf(partials.row) > 0 ? `colli da ${fmtQty(perColloOf(partials.row))} pz` : null,
                    ].filter(Boolean).join(' · ')}
                    onClose={() => setPartials(null)}
                >
                    {partials.rows === null ? (
                        <p className="muted">Caricamento…</p>
                    ) : (
                        <div className="pkg-table-wrap">
                            <table>
                                <thead>
                                    <tr className="table-head">
                                        <th>#</th>
                                        <th className="text-right">Pezzi residui</th>
                                        <th>Aperto il</th>
                                        <th>Operatore</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {partials.rows.map((pt, i) => (
                                        <tr key={pt.stock_uuid}>
                                            <td>{fmtQty(i + 1)}</td>
                                            <td className="text-right"><strong>{fmtQty(pt.qty_pieces_partial)}</strong> <span className="muted">{pt.component_um || ''}</span></td>
                                            <td>{pt.created_at ? new Date(Number(pt.created_at) * 1000).toLocaleDateString('it-IT') : ''}</td>
                                            <td>{[pt.operator_surname, pt.operator_name].filter(Boolean).join(' ')}</td>
                                        </tr>
                                    ))}
                                    {partials.rows.length === 0 && (
                                        <tr><td colSpan="4" className="muted" style={{ textAlign: 'center', padding: '20px' }}>Nessun collo aperto.</td></tr>
                                    )}
                                </tbody>
                                {partials.rows.length > 0 && (
                                    <tfoot>
                                        <tr className="pkg-total-row">
                                            <td>Totale</td>
                                            <td className="text-right">
                                                <strong>{fmtQty(partials.rows.reduce((sum, pt) => sum + Number(pt.qty_pieces_partial || 0), 0))}</strong>
                                            </td>
                                            <td colSpan="2" className="muted">
                                                {fmtQty(partials.rows.length)} {partials.rows.length === 1 ? 'collo aperto' : 'colli aperti'}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}
                </PkgModal>
            )}
        </div>
    );
};

export default ConsultazionePage;
